import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** @type {HTMLElement | null} */
  #homeHero = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateHomeHeroState = () => {
    if (!this.#homeHero) return;

    this.toggleAttribute('data-hero-passed', this.#homeHero.getBoundingClientRect().bottom <= 0);
  };

  /**
   * Keeps links to home-page sections inside the currently selected Shopify locale.
   * A Navigation link saved as `/#section` (or as an absolute shop URL) therefore
   * becomes `/ru/#section`, `/lv/#section`, etc. without duplicating menus.
   */
  #localizeHomeAnchors = () => {
    const localeMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
    const fallbackRoot = localeMatch ? `/${localeMatch[1]}/` : '/';
    const shopifyRoot = window.Shopify?.routes?.root;
    const rootUrl = new URL(
      typeof shopifyRoot === 'string' && shopifyRoot ? shopifyRoot : fallbackRoot,
      window.location.origin
    );
    const localizedRoot = rootUrl.pathname.endsWith('/') ? rootUrl.pathname : `${rootUrl.pathname}/`;

    for (const link of this.querySelectorAll('a[href*="#"]')) {
      const rawHref = link.getAttribute('href')?.trim();
      if (!rawHref) continue;

      let url;

      try {
        url = new URL(rawHref, window.location.href);
      } catch {
        continue;
      }

      if (url.origin !== window.location.origin || !url.hash) continue;

      const isHashOnly = rawHref.startsWith('#');
      const isLocalizedHome = /^\/[a-z]{2}(?:-[a-z]{2})?\/?$/i.test(url.pathname);
      const isHomeLink = url.pathname === '/' || url.pathname === localizedRoot || isLocalizedHome;

      if (isHashOnly || isHomeLink) {
        link.setAttribute('href', `${localizedRoot}${url.hash}`);
      }
    }
  };

  /**
   * Matches the mobile drawer label to the desktop `RU · EUR` order without
   * replacing the theme's large header-drawer snippet.
   */
  #formatMobileLocalizationLabel = () => {
    const label = this.querySelector('.drawer-localization__button--label');
    if (!(label instanceof HTMLElement)) return;

    const groups = [...label.children].filter((element) =>
      element.classList.contains('mobile-localization')
    );
    const countryGroup = groups.find((element) => element.querySelector('.currency-code'));
    const languageGroup = groups.find((element) => element !== countryGroup);

    if (!countryGroup || !languageGroup) return;

    let separator = [...label.children].find((element) => !groups.includes(element));

    if (!separator) {
      separator = document.createElement('span');
    }

    separator.classList.add('localization-code-separator');
    separator.textContent = '·';
    label.append(languageGroup, separator, countryGroup);
  };

  #updateScrollState = () => {
    this.#updateHomeHeroState();

    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    const desktopSubmenuOpen = this.querySelector(
      '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-haspopup="true"][aria-expanded="true"]'
    ) !== null;

    // Keep the header visible while an attached panel is being used. This also
    // prevents trackpad momentum from hiding a sticky header under its submenu.
    if (this.dataset.overlayOpen === 'localization' || desktopSubmenuOpen) {
      if (this.#offscreen || stickyMode === 'always') {
        this.dataset.stickyState = 'active';
      }
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isAtTop = scrollTop <= 1;

    // Ignore trackpad/touch momentum noise. The last position is intentionally
    // preserved so several small movements in one direction still accumulate.
    if (!isAtTop && Math.abs(scrollDelta) < 4) return;

    const isScrollingUp = scrollDelta < 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode !== 'always' && isAtTop) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      // show sticky header when scrolling up
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  #findHomeHero = () => {
    const heroContent = document.querySelector('.hbn-hero-content');
    const magicHero = document.querySelector('.hbn-magic-hero');
    const homeHero = magicHero || heroContent?.closest('.hero');
    this.toggleAttribute('data-has-magic-hero', magicHero instanceof HTMLElement);
    this.#homeHero = homeHero instanceof HTMLElement ? homeHero : null;
    this.toggleAttribute('data-has-home-hero', this.#homeHero !== null);
    this.#updateHomeHeroState();
  };

  connectedCallback() {
    super.connectedCallback();

    this.#localizeHomeAnchors();
    this.#formatMobileLocalizationLabel();

    this.#findHomeHero();
    document.addEventListener('shopify:section:load', this.#findHomeHero);

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    document.removeEventListener('shopify:section:load', this.#findHomeHero);
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
